import { RotateCcw, RotateCw } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { TimeInput } from "@/components/TimeInput";
import { SIGNS, SignMini, signQty } from "@/components/map/SignObjects";
import { pathM, fmtM } from "@/lib/geo";
import { SignDirection } from "@/components/DirectionQuestions";

export const SignForm = ({ el, onChange, readOnly, timing }) => {
  const meta = SIGNS[el.sign_type] || {};
  const set = (patch) => !readOnly && onChange({ ...el, ...patch });
  const rot = el.rotation || 0;
  return (
    <div className="space-y-3" data-testid="sign-form">
      <div>
        <Label className="text-[0.6875rem] text-slate-500">Tipo de objeto</Label>
        <div className="grid grid-cols-5 gap-1 mt-1">
          {Object.entries(SIGNS).filter(([, v]) => !!v.line === !!meta.line).map(([k, v]) => (
            <button key={k} type="button" title={v.label} onClick={() => set({ sign_type: k })} data-testid={`sign-type-${k}`}
              className={`flex flex-col items-center gap-1 py-1.5 rounded-md border text-[0.5625rem] transition-colors ${el.sign_type === k ? "border-amber-400 bg-amber-500/15 text-amber-800" : "border-slate-200 text-slate-500 hover:bg-slate-100"}`}>
              <SignMini type={k} rot={rot} size={20} />{v.code}
            </button>
          ))}
        </div>
      </div>
      {el.sign_type === "r101" && <SignDirection el={el} onChange={onChange} readOnly={readOnly} />}
      {meta.rotatable && (
        <div className="space-y-2" data-testid="sign-rotation">
          <Label className="text-[0.6875rem] text-slate-500">Orientación de la flecha · {rot}°</Label>
          <div className="flex items-center gap-2">
            <button type="button" onClick={() => set({ rotation: (rot + 315) % 360 })} className="p-1.5 rounded-md bg-slate-50 hover:bg-slate-100" data-testid="sign-rotate-left"><RotateCcw className="w-4 h-4" /></button>
            <Slider value={[rot]} min={0} max={359} step={5} onValueChange={([v]) => set({ rotation: v })} className="flex-1" data-testid="sign-rotation-slider" />
            <button type="button" onClick={() => set({ rotation: (rot + 45) % 360 })} className="p-1.5 rounded-md bg-slate-50 hover:bg-slate-100" data-testid="sign-rotate-right"><RotateCw className="w-4 h-4" /></button>
          </div>
        </div>
      )}
      {meta.line && (
        <div className="grid grid-cols-3 gap-2 items-end" data-testid="sign-line-info">
          <div><Label className="text-[0.6875rem] text-slate-500">Separación (m)</Label>
            <Input type="number" min="0.5" step="0.5" value={el.spacing ?? 2.5} onChange={(e) => set({ spacing: e.target.value === "" ? "" : Math.max(0.5, Number(e.target.value)) })} className="h-9 mt-1" data-testid="sign-spacing-input" /></div>
          <div className="bg-slate-50 rounded-lg p-2"><div className="text-[0.625rem] text-slate-500">LONGITUD</div><b className="text-slate-900 text-sm">{fmtM(pathM(el.coords || []))}</b></div>
          <div className="bg-slate-50 rounded-lg p-2"><div className="text-[0.625rem] text-slate-500">VALLAS</div><b className="text-amber-700 text-sm" data-testid="sign-line-count">{signQty(el)}</b></div>
        </div>
      )}
      {meta.qty && (
        <div>
          <Label className="text-[0.6875rem] text-slate-500">Cantidad</Label>
          <Input type="number" min="1" value={el.qty || 1} onChange={(e) => set({ qty: Math.max(1, Number(e.target.value) || 1) })} className="h-9 mt-1" data-testid="sign-qty-input" />
        </div>
      )}
      <div className="grid grid-cols-2 gap-2">
        <div><Label className="text-[0.6875rem] text-slate-500">Desde</Label><TimeInput value={el.start_time || ""} onChange={(e) => set({ start_time: e.target.value })} className="h-9 mt-1" data-testid="sign-start-input" /></div>
        <div><Label className="text-[0.6875rem] text-slate-500">Hasta</Label><TimeInput value={el.end_time || ""} onChange={(e) => set({ end_time: e.target.value })} className="h-9 mt-1" data-testid="sign-end-input" /></div>
      </div>
      <div className="text-[0.625rem] text-slate-500">Vacío = mismo horario que el corte más cercano (200 m) o el del evento. Genera tarea de montaje y de retirada.</div>
      {timing && <div className="font-mono text-[0.6875rem] text-emerald-700" data-testid="element-computed-timing">Plan: {timing.start_hm} – {timing.end_hm} ({timing.source})</div>}
    </div>
  );
};
